import { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, addDoc, deleteDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../config/firebase';
import { scoreAndRankTenants } from '../utils/scoringEngine';
import { getRecommendedAction, generateInsight } from '../utils/actionRecommender';
import { calculatePaymentFingerprint } from '../utils/paymentFingerprint';
import { analyzeDrift } from '../utils/driftDetector';
import { generateMessageId } from '../utils/generateId';
import { prepareTenantForCreate, prepareTenantForUpdate } from '../utils/tenantWritePrep';

export function useTenants(propertyId) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);

    if (!propertyId) {
      setTenants([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const tenantsRef = collection(db, 'properties', propertyId, 'tenants');
    const unsubscribe = onSnapshot(
      query(tenantsRef),
      (snapshot) => {
        try {
          const rawTenants = snapshot.docs.map((documentSnapshot) => ({
            id: documentSnapshot.id,
            ...documentSnapshot.data(),
          }));

          const enrichedTenants = rawTenants.map((tenant) => ({
            ...tenant,
            fingerprint: calculatePaymentFingerprint(tenant.paymentHistory),
            drift: analyzeDrift(tenant.paymentHistory),
            currentDaysLate: calculateCurrentDaysLate(tenant),
          }));

          const scoredTenants = scoreAndRankTenants(enrichedTenants);
          const tenantsWithActions = scoredTenants.map((tenant) => ({
            ...tenant,
            recommendation: getRecommendedAction(tenant),
            insight: generateInsight(tenant),
          }));

          setTenants(tenantsWithActions);
          setLoading(false);
        } catch (listenerError) {
          console.error('Error processing tenants:', listenerError);
          setError(listenerError.message);
          setLoading(false);
        }
      },
      (listenerError) => {
        console.error('Firestore listener error:', listenerError);
        setError(listenerError.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [propertyId]);

  const ensurePropertyId = () => {
    if (!propertyId) {
      throw new Error('Select a property before editing tenants.');
    }
  };

  const addTenant = async (tenantData) => {
    ensurePropertyId();
    const ref = collection(db, 'properties', propertyId, 'tenants');
    return addDoc(ref, { ...prepareTenantForCreate(tenantData), createdAt: new Date().toISOString() });
  };

  const addTenants = async (tenantsArray) => {
    ensurePropertyId();
    const ref = collection(db, 'properties', propertyId, 'tenants');
    return Promise.all(
      tenantsArray.map((tenant) => addDoc(ref, { ...prepareTenantForCreate(tenant), createdAt: new Date().toISOString() }))
    );
  };

  const updateTenant = async (tenantId, updates) => {
    ensurePropertyId();
    return updateDoc(doc(db, 'properties', propertyId, 'tenants', tenantId), prepareTenantForUpdate(updates));
  };

  const deleteTenant = async (tenantId) => {
    ensurePropertyId();
    return deleteDoc(doc(db, 'properties', propertyId, 'tenants', tenantId));
  };

  const logOutreach = async (tenantId, outreachEntry) => {
    ensurePropertyId();
    const tenantRef = doc(db, 'properties', propertyId, 'tenants', tenantId);
    const tenant = tenants.find((item) => item.id === tenantId);
    const currentLog = tenant?.outreachLog || [];
    const messageId = outreachEntry.messageId || generateMessageId();

    return updateDoc(tenantRef, {
      outreachLog: [...currentLog, { ...outreachEntry, messageId, date: new Date().toISOString() }],
    });
  };

  const recordPayment = async (tenantId, { amount, date, note = '' }) => {
    ensurePropertyId();
    const tenantRef = doc(db, 'properties', propertyId, 'tenants', tenantId);
    const tenant = tenants.find((item) => item.id === tenantId);
    if (!tenant) {
      throw new Error('Tenant not found');
    }

    const newBalance = Math.max((tenant.currentBalance || 0) - amount, 0);
    const now = new Date().toISOString();
    const daysLate = tenant.currentDaysLate || 0;

    return updateDoc(tenantRef, {
      currentBalance: newBalance,
      paymentHistory: arrayUnion({
        date: date || now.slice(0, 10),
        amount,
        daysLate,
        note,
        recordedAt: now,
      }),
      outreachLog: arrayUnion({
        channel: 'payment',
        status: 'received',
        amount,
        messageId: generateMessageId(),
        message: `Payment of $${amount.toFixed(2)} received${note ? ` - ${note}` : ''}. New balance: $${newBalance.toFixed(2)}.`,
        date: now,
        recoveryEvent: true,
        source: 'manual',
      }),
    });
  };

  return { tenants, loading, error, addTenant, addTenants, updateTenant, deleteTenant, logOutreach, recordPayment };
}

function calculateCurrentDaysLate(tenant) {
  if (!tenant.currentBalance || tenant.currentBalance <= 0) {
    return 0;
  }

  const now = new Date();
  const paidEntries = (tenant.paymentHistory || [])
    .filter((payment) => payment.amount > 0 && payment.date)
    .sort((left, right) => new Date(left.date) - new Date(right.date));

  if (paidEntries.length === 0) {
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return Math.max(0, Math.floor((now - firstOfMonth) / 86400000));
  }

  const lastPaid = new Date(paidEntries[paidEntries.length - 1].date);
  const nextDueMonth = lastPaid.getMonth() + 1;
  const nextDueYear = lastPaid.getFullYear() + Math.floor(nextDueMonth / 12);
  const nextDueDate = new Date(nextDueYear, nextDueMonth % 12, 1);
  if (now < nextDueDate) {
    return 0;
  }

  return Math.floor((now - nextDueDate) / 86400000);
}
